import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faCheck, faRotateLeft, faTrash } from "@fortawesome/free-solid-svg-icons";
import type { ReconciliationGridRow } from "./reconciliationGridTypes";
import { ReconciliationActionIcons } from "./reconciliationGridTypes";

export interface ReconciliationRowActionProps {
    row: ReconciliationGridRow;
    onStage: (row: ReconciliationGridRow) => void;
    onUndoStaged?: (changeKey: string) => void;
}

export function ReconciliationRowAction({ row, onStage, onUndoStaged }: ReconciliationRowActionProps) {
    if (row.actionIcon === ReconciliationActionIcons.None) {
        return null;
    }

    if (row.actionIcon === ReconciliationActionIcons.Match) {
        return (
            <span className="text-success" title="Match" aria-label="Match">
                <FontAwesomeIcon icon={faCheck} />
            </span>
        );
    }

    if (row.actionIcon === ReconciliationActionIcons.StagedSync && row.stagedChangeKey) {
        return (
            <OverlayTrigger overlay={<Tooltip>Revert pending change</Tooltip>}>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onUndoStaged?.(row.stagedChangeKey!)}
                    aria-label="Revert pending change"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </Button>
            </OverlayTrigger>
        );
    }

    if (row.actionIcon === ReconciliationActionIcons.StagedDelete && row.stagedChangeKey) {
        return (
            <OverlayTrigger overlay={<Tooltip>Revert pending delete</Tooltip>}>
                <Button
                    variant="danger"
                    size="sm"
                    onClick={() => onUndoStaged?.(row.stagedChangeKey!)}
                    aria-label="Revert pending delete"
                >
                    <FontAwesomeIcon icon={faRotateLeft} />
                </Button>
            </OverlayTrigger>
        );
    }

    const tooltip =
        row.actionDisabledReason ??
        (row.actionIcon === ReconciliationActionIcons.SoftDelete
            ? "Mark for removal from reg data"
            : "Apply template value to system");

    const variant = row.actionIcon === ReconciliationActionIcons.SoftDelete ? "outline-danger" : "outline-primary";
    const icon = row.actionIcon === ReconciliationActionIcons.SoftDelete ? faTrash : faArrowRight;
    const label =
        row.actionIcon === ReconciliationActionIcons.SoftDelete
            ? "Mark for removal from reg data"
            : "Apply template value to system";

    return (
        <OverlayTrigger overlay={<Tooltip>{row.actionDisabled ? row.actionDisabledReason ?? tooltip : tooltip}</Tooltip>}>
            <span>
                <Button
                    variant={variant}
                    size="sm"
                    disabled={row.actionDisabled}
                    onClick={() => onStage(row)}
                    aria-label={label}
                >
                    <FontAwesomeIcon icon={icon} />
                </Button>
            </span>
        </OverlayTrigger>
    );
}
